import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { CATEGORIES } from "../data/services";
import { scrollToId } from "../lib/ui";

const LINKS = [
  { id: "proces", label: "Sådan fungerer det" },
  { id: "om-juris", label: "Om Juris" },
  { id: "kontakt", label: "Kontakt" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 60);
    update();
    const lenis = window.__lenis;
    const onLenis = (e) => setScrolled((e?.scroll ?? window.scrollY) > 60);
    if (lenis?.on) lenis.on("scroll", onLenis);
    window.addEventListener("scroll", update, { passive: true });
    const iv = setInterval(update, 400);
    return () => {
      if (lenis?.off) lenis.off("scroll", onLenis);
      window.removeEventListener("scroll", update);
      clearInterval(iv);
    };
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    setMenuOpen(false);
    if (id === "kontakt") {
      navigate("/kontakt");
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 120);
    } else {
      scrollToId(id);
    }
  };

  const toYdelser = (e, slug) => {
    e?.preventDefault?.();
    setOpen(false);
    setMenuOpen(false);
    if (slug) {
      navigate(`/ydelser/${slug}`);
    } else {
      navigate("/ydelser");
    }
  };

  const dark = !scrolled;

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-slate-200/80 bg-white/90 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between gap-4 px-6 sm:px-8 lg:px-12">
        <a href="#top" onClick={(e) => go(e, "top")} aria-label="Juris — til forsiden" data-testid="logo-home">
          <Logo dark={dark} />
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Hovedmenu">
          <div className="relative">
            <button
              data-testid="nav-link-ydelser"
              onClick={() => setMenuOpen(!menuOpen)}
              onMouseEnter={() => setMenuOpen(true)}
              className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
                dark ? "text-white/85 hover:text-white" : "text-slate-600 hover:text-brand-600"
              }`}
            >
              Ydelser
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>
            {menuOpen && (
              <div
                data-testid="nav-ydelser-dropdown"
                onMouseLeave={() => setMenuOpen(false)}
                className="absolute left-1/2 top-full z-50 mt-3 w-[720px] -translate-x-1/2 rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl shadow-slate-900/10"
              >
                <div className="grid grid-cols-4 gap-6">
                  {CATEGORIES.map((c) => (
                    <div key={c.slug}>
                      <button
                        data-testid={`nav-ydelser-cat-${c.slug}`}
                        onClick={(e) => toYdelser(e, c.slug)}
                        className="text-left text-sm font-semibold text-slate-900 transition-colors hover:text-brand-600"
                      >
                        {c.title}
                      </button>
                      <ul className="mt-3 space-y-2">
                        {c.items.map((p) => (
                          <li key={p.slug}>
                            <button
                              data-testid={`nav-ydelser-item-${p.slug}`}
                              onClick={(e) => toYdelser(e, p.slug)}
                              className="text-left text-[13px] leading-snug text-slate-500 transition-colors hover:text-brand-600"
                            >
                              {p.title}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                  <button
                    data-testid="nav-ydelser-all"
                    onClick={(e) => toYdelser(e)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600"
                  >
                    Se alle ydelser
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                  <a
                    href="https://arrea.dk"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="nav-ydelser-inkasso"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 transition-colors hover:text-brand-600"
                  >
                    Inkasso
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            )}
          </div>

          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.id === "kontakt" ? "/kontakt" : `#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              onClick={(e) => go(e, l.id)}
              className={`text-sm font-medium transition-colors ${
                dark ? "text-white/85 hover:text-white" : "text-slate-600 hover:text-brand-600"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta"
            onClick={(e) => go(e, "kontakt")}
            className={`inline-flex items-center gap-1.5 rounded-full bg-brand-950 px-5 py-2.5 text-sm font-semibold text-white ring-1 transition-all duration-200 hover:bg-brand-900 ${
              dark ? "ring-white/25" : "ring-brand-950/10"
            }`}
          >
            Kontakt Juris
            <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Luk menu" : "Åbn menu"}
          onClick={() => setOpen(!open)}
          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border md:hidden ${
            dark ? "border-white/25 text-white" : "border-slate-200 bg-white text-slate-700"
          }`}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="max-h-[80vh] overflow-y-auto border-t border-slate-200 bg-white px-4 pb-6 pt-2 md:hidden">
          <button
            data-testid="nav-mobile-link-ydelser"
            onClick={(e) => toYdelser(e)}
            className="flex w-full items-center justify-between border-b border-slate-100 py-3.5 text-base font-medium text-slate-700"
          >
            Ydelser
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </button>
          <div className="border-b border-slate-100 py-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {CATEGORIES.map((c) => (
                <div key={c.slug}>
                  <button data-testid={`nav-mobile-cat-${c.slug}`} onClick={(e) => toYdelser(e, c.slug)} className="text-left text-sm font-semibold text-slate-900">
                    {c.title}
                  </button>
                  <ul className="mt-1.5 space-y-1">
                    {c.items.map((p) => (
                      <li key={p.slug}>
                        <button data-testid={`nav-mobile-item-${p.slug}`} onClick={(e) => toYdelser(e, p.slug)} className="text-left text-[13px] text-slate-500">
                          {p.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <a
            href="https://arrea.dk"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-mobile-inkasso"
            onClick={() => setOpen(false)}
            className="flex items-center justify-between border-b border-slate-100 py-3.5 text-base font-medium text-slate-700"
          >
            Inkasso
            <ArrowUpRight className="h-4 w-4 text-slate-400" />
          </a>
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.id === "kontakt" ? "/kontakt" : `#${l.id}`}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={(e) => go(e, l.id)}
              className="block border-b border-slate-100 py-3.5 text-base font-medium text-slate-700"
            >
              {l.label}
            </a>
          ))}
          <button data-testid="nav-mobile-cta" onClick={(e) => go(e, "kontakt")} className="mt-4 w-full rounded-full bg-brand-950 px-5 py-3 text-sm font-semibold text-white">
            Kontakt Juris
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;